import * as React from 'react';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {
    ENTITY_TYPE_SELECT_OPTIONS,
    ALL_ENTITY_CUSTOM_FIELD_SELECT_OPTIONS,
    LEAD_CUSTOM_FIELD_SELECT_OPTIONS,
} from 'shells/create-custom-field/consts';
import type {CustomFieldTypeOption} from 'shells/create-custom-field/types';
import {TwoPaneModalForm} from 'shells/modal/two-pane-modal-form';

import {useGetStagesets} from '../../../core/select/hooks/use-get-stagesets';

import {TAB_GRAPHQL_MAP} from '../custom-fields';
import {useGetCustomFieldById, useUpdateCustomField, useCreateCustomField} from '../graphql/hooks';

import {LeftSection} from './left-section';
import {RightSection} from './right-section';

const PIPELINE_TYPE_ALL = 'all';
export const PIPELINE_TYPE_SPECIFIC = 'specific';

export const PIPELINE_SELECT_OPTIONS = [
    {label: 'All pipelines', value: PIPELINE_TYPE_ALL},
    {label: 'Specific pipelines', value: PIPELINE_TYPE_SPECIFIC},
] as const;

const ENTITY_TYPE_OPTIONS_MAPPING = {
    // @ts-expect-error
    company: ENTITY_TYPE_SELECT_OPTIONS[0].value,
    // @ts-expect-error
    person: ENTITY_TYPE_SELECT_OPTIONS[1].value,
    // @ts-expect-error
    lead: ENTITY_TYPE_SELECT_OPTIONS[2].value,
} as const;

const ENTITY_SELECT_GRAPHQL_MAP = {
    Accounts: 'ACCOUNT',
    Contacts: 'CONTACT',
    Leads: 'LEAD',
} as const;

type Props = {
    onClose: () => void;
    entityType: string;
    isEditing: boolean;
    fieldId: string | null | undefined;
    refetchStagesets: () => Promise<any>;
    canUpdateMultipleChoicesSetting?: boolean;
};

export function CreateOrEditModal(props: Props) {
    const {onClose, entityType, refetchStagesets, canUpdateMultipleChoicesSetting = true} = props;

    const [selectedEntity, setSelectedEntity] = React.useState<string>(
        // @ts-expect-error
        ENTITY_TYPE_OPTIONS_MAPPING[props.entityType] || ENTITY_TYPE_SELECT_OPTIONS[0].value
    );
    const [selectedCustomFieldOption, setSelectedCustomFieldOption] =
        // @ts-expect-error
        React.useState<CustomFieldTypeOption>(ALL_ENTITY_CUSTOM_FIELD_SELECT_OPTIONS[0]);
    const [decisionFieldChoices, setDecisionFieldChoices] = React.useState<string[]>([]);
    const [decisionFieldAllowMultiple, setDecisionFieldAllowMultiple] = React.useState(false);
    const [selectedPipelineType, setSelectedPipelineType] =
        React.useState<string>(PIPELINE_TYPE_ALL);
    const [selectedStagesets, setSelectedStagesets] = React.useState<string[]>([]);
    const [name, setName] = React.useState<string>('');
    const [description, setDescription] = React.useState<string>('');
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const {stagesets} = useGetStagesets();
    const {customField, isLoading} = useGetCustomFieldById(props.fieldId);
    const {createCustomField} = useCreateCustomField();
    const {updateCustomField} = useUpdateCustomField();

    React.useEffect(() => {
        if (props.isEditing && customField) {
            setName(customField.attribute.name);
            if (customField.attribute.description != null) {
                setDescription(customField.attribute.description);
            }

            const matchingOption = [
                ...ALL_ENTITY_CUSTOM_FIELD_SELECT_OPTIONS,
                ...LEAD_CUSTOM_FIELD_SELECT_OPTIONS,
            ].find((option) => option.value === customField.attribute.type);

            if (matchingOption) {
                setSelectedCustomFieldOption(matchingOption);
            }

            if (customField.__typename === 'EntityAttributeEnum' && customField.options) {
                setDecisionFieldAllowMultiple(Boolean(customField.allowMultiple));
                setDecisionFieldChoices(customField.options.map((option) => option.name));
            }

            if (customField.stagesets) {
                const hasSpecificPipelines = customField.stagesets.isStagesetSpecific;

                setSelectedPipelineType(
                    hasSpecificPipelines ? PIPELINE_TYPE_SPECIFIC : PIPELINE_TYPE_ALL
                );

                if (hasSpecificPipelines && customField.stagesets) {
                    const selectedPipelineIds = customField.stagesets.edges
                        .filter((edge) => edge.isOnPipeline)
                        .map((edge) => edge.node.id);
                    setSelectedStagesets(selectedPipelineIds);
                }
            }
        }

        return () => {
            setIsSubmitting(false);
        };
    }, [props.isEditing, customField]);

    const customFieldSelectOptions = [...ALL_ENTITY_CUSTOM_FIELD_SELECT_OPTIONS];
    if (selectedEntity === 'Leads') {
        customFieldSelectOptions.push(...LEAD_CUSTOM_FIELD_SELECT_OPTIONS);
    }

    const handleCreate = React.useCallback(() => {
        createCustomField(
            {
                customField: {
                    name,
                    description,
                    type: selectedCustomFieldOption.value,
                    entityName: selectedEntity,
                    choices: decisionFieldChoices,
                    allowMultiple: Boolean(decisionFieldAllowMultiple),
                    pipelines:
                        selectedPipelineType === PIPELINE_TYPE_SPECIFIC ? selectedStagesets : null,
                },
            },
            // @ts-expect-error
            ENTITY_SELECT_GRAPHQL_MAP[selectedEntity]
        )
            .then(() => {
                if (selectedEntity === 'Leads') {
                    refetchStagesets();
                }
                onClose();
            })
            .catch((error) => {
                setIsSubmitting(false);
                setErrorMessage(safelyGetGraphQLErrorMessage(error));
            });
    }, [
        createCustomField,
        name,
        description,
        selectedCustomFieldOption.value,
        selectedEntity,
        decisionFieldChoices,
        decisionFieldAllowMultiple,
        selectedPipelineType,
        selectedStagesets,
        onClose,
        refetchStagesets,
    ]);

    const handleEdit = React.useCallback(() => {
        if (!props.fieldId) {
            setIsSubmitting(false);
            return;
        }

        return (
            updateCustomField(
                {
                    id: props.fieldId,
                    edits: {
                        name,
                        description,
                        allowMultiple:
                            selectedCustomFieldOption.value === 'ENUM'
                                ? Boolean(decisionFieldAllowMultiple)
                                : undefined,
                        choices:
                            selectedCustomFieldOption.value === 'ENUM'
                                ? decisionFieldChoices
                                : undefined,
                        pipelines:
                            selectedPipelineType === PIPELINE_TYPE_SPECIFIC
                                ? selectedStagesets
                                : null,
                    },
                },
                // @ts-expect-error
                TAB_GRAPHQL_MAP[entityType]
            )
                .then(() => {
                    onClose();
                })
                // @ts-expect-error
                .catch((error) => {
                    setIsSubmitting(false);
                    setErrorMessage(safelyGetGraphQLErrorMessage(error));
                })
        );
    }, [
        props.fieldId,
        name,
        description,
        selectedCustomFieldOption,
        decisionFieldAllowMultiple,
        decisionFieldChoices,
        selectedPipelineType,
        selectedStagesets,
        updateCustomField,
        onClose,
        entityType,
    ]);

    const leftSection = (
        <LeftSection
            name={name}
            setName={setName}
            entityType={selectedEntity}
            setEntityType={setSelectedEntity}
            customFieldSelectOptions={customFieldSelectOptions}
            selectedCustomFieldOption={selectedCustomFieldOption}
            setSelectedCustomFieldOption={setSelectedCustomFieldOption}
            decisionFieldChoices={decisionFieldChoices}
            setDecisionFieldChoices={setDecisionFieldChoices}
            decisionFieldAllowMultiple={decisionFieldAllowMultiple}
            setDecisionFieldAllowMultiple={setDecisionFieldAllowMultiple}
            description={description}
            setDescription={setDescription}
            isEditing={props.isEditing}
            canUpdateMultipleChoicesSetting={canUpdateMultipleChoicesSetting}
        />
    );

    const rightSection = (
        <RightSection
            selectedEntity={selectedEntity}
            stagesets={stagesets}
            selectedPipelineType={selectedPipelineType}
            setSelectedPipelineType={setSelectedPipelineType}
            selectedStagesets={selectedStagesets}
            setSelectedStagesets={setSelectedStagesets}
            isEditing={props.isEditing}
        />
    );

    const header = props.isEditing ? 'Edit custom field' : 'Create custom field';
    const submitButtonText = props.isEditing ? 'Save' : 'Create';

    const isSaveButtonDisabled =
        !name || (selectedCustomFieldOption.value === 'ENUM' && decisionFieldChoices.length === 0);

    return (
        <TwoPaneModalForm
            headerText={header}
            submitButtonText={submitButtonText}
            onSubmit={() => {
                if (!selectedEntity) {
                    return;
                }
                setIsSubmitting(true);
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                props.isEditing ? handleEdit() : handleCreate();
            }}
            saveButtonDisabled={isSaveButtonDisabled}
            onClose={props.onClose}
            leftSection={leftSection}
            rightSection={rightSection}
            // @ts-expect-error
            isOpen={true}
            errorMessage={errorMessage}
            minHeight={600}
            isLoading={isLoading || isSubmitting}
        />
    );
}
